/**
 * VersionDetailPage -- Screen H2: Version Detail
 * Route: /content-studio/versions/:id
 * Template: 3a-detail-editor
 * Step 6F Green Phase (9F integration: useParams for route-based loading, consistent main/h1)
 *
 * Tabbed layout: Validation Results, Audit History
 * - Validation Results tab: shows blocking errors and warnings
 * - Audit History tab: shows chronological audit entries
 *
 * Uses 6F PublishButton (from ValidationDashboard) for the publish trigger.
 * Keeps a confirm dialog (matching the 6F ValidationDashboard pattern) because
 * this page needs a confirmation step before publishing.
 *
 * RBAC feature gating:
 * - Content Admin / Super Admin: all actions (edit, publish, create draft, archive)
 */

import React, { useEffect, useState } from 'react';
import { PageSpinner } from '../../components/PageSpinner';
import { useParams, Link, useNavigate } from 'react-router-dom';
// Link kept for Validation Dashboard link below
import { useAuthStore } from '../../stores/authStore';
import { useVersionStore } from '../../stores/versionStore';
import { apiClient, ServiceError, type ReferenceImpact } from '../../services/apiClient';
import { RIBlockedDialog } from '../../components/RIBlockedDialog';
import { showToast } from '../../lib/toast';
import { StatusBadge } from '../../components/StatusBadge';
import { PublishButton } from '../../components/PublishButton';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ArrowLeft, Plus, X, Loader2, ShieldCheck, Check } from 'lucide-react';
import type { Version, FacultyActivationThreshold } from '../../types';

// ---------------------------------------------------------------------------
// TagChips — reusable editable tag list (from VersionConfigPage)
// ---------------------------------------------------------------------------

function TagChips({
  label,
  tags,
  onAdd,
  onRemove,
  readOnly,
  placeholder = 'Enter value...',
}: {
  label: string;
  tags: string[];
  onAdd: (tag: string) => void;
  onRemove: (index: number) => void;
  readOnly: boolean;
  placeholder?: string;
}) {
  const [input, setInput] = useState('');

  const handleAdd = () => {
    if (input.trim() && !tags.includes(input.trim())) {
      onAdd(input.trim());
      setInput('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      {!readOnly && (
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 h-9 border border-brand-steel/50 bg-white rounded-lg px-3 text-sm placeholder:text-brand-steel/50 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson"
            aria-label={`Add ${label.toLowerCase()}`}
          />
          <Button
            type="button"
            onClick={handleAdd}
            disabled={!input.trim()}
            className="h-9 px-3 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-lg inline-flex items-center gap-1.5"
          >
            <Plus size={14} aria-hidden="true" /> Add
          </Button>
        </div>
      )}
      {tags.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, idx) => (
            <span
              key={`${tag}-${idx}`}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-medium bg-brand-panel text-gray-700 border border-brand-steel/50"
            >
              {tag}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  className="text-brand-steel hover:text-brand-error"
                  aria-label={`Remove ${tag}`}
                >
                  <X size={14} />
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-brand-steel/70">No items added yet.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ThresholdTable — editable activation thresholds (from VersionConfigPage)
// ---------------------------------------------------------------------------

const POINTS_RANGE_RE = /^(>=?\d+|\d+-\d+)$/;

function isValidPointsRange(value: string): boolean {
  return POINTS_RANGE_RE.test(value.trim());
}

function ThresholdTable({
  thresholds,
  onChange,
  readOnly,
  showRowErrors = false,
}: {
  thresholds: FacultyActivationThreshold[];
  onChange: (thresholds: FacultyActivationThreshold[]) => void;
  readOnly: boolean;
  showRowErrors?: boolean;
}) {
  const updateRow = (idx: number, field: keyof FacultyActivationThreshold, value: string) => {
    onChange(thresholds.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  const addRow = () => {
    onChange([...thresholds, { points_range: '', faculties_activated: '' }]);
  };

  const removeRow = (idx: number) => {
    onChange(thresholds.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700">Faculty Activation Thresholds</label>
        {!readOnly && (
          <button
            type="button"
            onClick={addRow}
            className="text-xs text-brand-crimson hover:underline inline-flex items-center gap-1"
          >
            <Plus size={12} aria-hidden="true" /> Add row
          </button>
        )}
      </div>
      <div className="border border-brand-steel/50 bg-white rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-brand-steel/50 bg-brand-panel">
              <th className="text-xs font-semibold text-brand-steel/70 uppercase tracking-wider px-3 py-2 text-left">Points Range</th>
              <th className="text-xs font-semibold text-brand-steel/70 uppercase tracking-wider px-3 py-2 text-left">Faculties Activated</th>
              {!readOnly && <th className="w-10" />}
            </tr>
          </thead>
          <tbody>
            {thresholds.map((t, idx) => (
              <tr key={idx} className="border-b border-brand-steel/50 last:border-b-0">
                <td className="px-3 py-2">
                  {readOnly ? (
                    <span className="text-gray-900">{t.points_range}</span>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={t.points_range}
                        onChange={e => updateRow(idx, 'points_range', e.target.value)}
                        className={`w-full border bg-white rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson ${showRowErrors && (!t.points_range.trim() || !isValidPointsRange(t.points_range)) ? 'border-brand-error' : 'border-brand-steel/50'}`}
                        placeholder="e.g. 0-5"
                        aria-label={`Row ${idx + 1} points range`}
                      />
                      {showRowErrors && t.points_range.trim() && !isValidPointsRange(t.points_range) && (
                        <p className="text-xs text-brand-error mt-0.5">{"Expected: 0-5, >20, or >=10"}</p>
                      )}
                    </>
                  )}
                </td>
                <td className="px-3 py-2">
                  {readOnly ? (
                    <span className="text-gray-900">{t.faculties_activated}</span>
                  ) : (
                    <input
                      type="text"
                      value={t.faculties_activated}
                      onChange={e => updateRow(idx, 'faculties_activated', e.target.value)}
                      className={`w-full border bg-white rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson ${showRowErrors && !t.faculties_activated.trim() ? 'border-brand-error' : 'border-brand-steel/50'}`}
                      placeholder="e.g. F1+F2"
                      aria-label={`Row ${idx + 1} faculties activated`}
                    />
                  )}
                </td>
                {!readOnly && (
                  <td className="px-2 py-2">
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      aria-label={`Remove threshold row ${idx + 1}`}
                      className="text-brand-steel hover:text-brand-error"
                    >
                      <X size={14} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {thresholds.length === 0 && (
              <tr>
                <td colSpan={readOnly ? 2 : 3} className="px-3 py-4 text-center text-brand-steel text-xs">
                  No thresholds defined.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types for tab data
// ---------------------------------------------------------------------------

interface ValidationCheck {
  name: string;
  passed: boolean;
}

interface ValidationResponse {
  version_id?: string;
  status: string;
  checks?: ValidationCheck[];
  blocking_errors?: Array<{ condition: string; severity: string }>;
  blocking_conditions?: Array<{ condition: string; severity?: string }>;
  warnings?: Array<{ condition: string }>;
}

import {
  type AuditLogEntry as AuditEntry,
  formatAuditTitle,
  formatChangeSummary,
  formatRelativeTime,
  getActionPresentation,
  getCategoryLabel,
} from "../../utils/audit";

function formatFieldSummary(fields?: Array<{ field: string; old_value: string; new_value: string }>): string {
  if (!fields || fields.length === 0) return "Properties updated";
  if (fields.length === 1) return `${fields[0].field} changed`;
  return `${fields.map((f) => f.field).join(", ")} changed`;
}

export function VersionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const storeVersion = useVersionStore(s => s.selectedVersion);
  const setSelectedVersion = useVersionStore(s => s.setSelectedVersion);
  const triggerRefresh = useVersionStore(s => s.triggerRefresh);
  const [version, setVersion] = useState<Version | null>(storeVersion);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [riBlock, setRiBlock] = useState<{ title: string; impacts: ReferenceImpact[] } | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  // Names of inactive SRSs that will be pruned at publish. Loaded when
  // the publish dialog opens so the author sees the destructive impact
  // before confirming. null = not yet loaded or zero inactives.
  const [inactiveSrsToPrune, setInactiveSrsToPrune] = useState<string[] | null>(null);

  // Tab data
  const [validation, setValidation] = useState<ValidationResponse | null>(null);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);

  // Configuration tab state
  const [facultyLevels, setFacultyLevels] = useState(3);
  const [answerOptionTags, setAnswerOptionTags] = useState<string[]>([]);
  const [answerOptionsPerTag, setAnswerOptionsPerTag] = useState(2);
  const [variantTypes, setVariantTypes] = useState<string[]>([]);
  const [thresholds, setThresholds] = useState<FacultyActivationThreshold[]>([]);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [configErrors, setConfigErrors] = useState<{ answerOptionTags?: string; variantTypes?: string; thresholds?: string }>({});
  const [showThresholdRowErrors, setShowThresholdRowErrors] = useState(false);

  // Editable version name + changelog (Draft only)
  const [editVersionName, setEditVersionName] = useState('');
  const [editChangelog, setEditChangelog] = useState('');
  const [isSavingMeta, setIsSavingMeta] = useState(false);

  // Load version from API using route param or store
  const versionId = id ?? storeVersion?.id;

  useEffect(() => {
    let cancelled = false;
    if (!versionId) {
      setIsLoading(false);
      return () => { cancelled = true; };
    }
    apiClient.get<Version>(`/api/versions/${versionId}`)
      .then(v => { if (!cancelled) { setVersion(v); } })
      .catch(err => { if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Failed to load'); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [versionId]);

  // Initialize config state from loaded version
  useEffect(() => {
    if (!version) return;
    setFacultyLevels(version.faculty_levels_per_activity ?? 3);
    setAnswerOptionTags(version.answer_option_tags ?? []);
    setAnswerOptionsPerTag(version.answer_options_per_tag ?? 2);
    setVariantTypes(version.variant_types ?? []);
    setThresholds(version.faculty_activation_thresholds ?? []);
    setEditVersionName(version.version_name ?? '');
    setEditChangelog(version.changelog_notes ?? '');
  }, [version?.id]);

  // Fetch validation
  useEffect(() => {
    if (!versionId) return;
    let cancelled = false;
    apiClient.get<ValidationResponse>(`/api/validation/${versionId}`)
      .then(data => { if (!cancelled) setValidation(data); })
      .catch(() => { /* non-critical */ });
    return () => { cancelled = true; };
  }, [versionId]);

  // Fetch audit history
  useEffect(() => {
    if (!versionId) return;
    let cancelled = false;
    apiClient.get<AuditEntry[]>(`/api/versions/${versionId}/audit`)
      .then(data => { if (!cancelled) setAuditEntries(data); })
      .catch(() => { /* non-critical */ });
    return () => { cancelled = true; };
  }, [versionId]);

  // Role-based permission checks
  const userRoles = user?.roles ?? [];
  const canAdmin = userRoles.some(r =>
    ['Content Admin', 'Super Admin'].includes(r),
  );
  const canEdit = canAdmin;
  const canPublish = canAdmin;

  const isDraft = version?.state === 'Draft';
  const isActive = version?.state === 'Active';


  const handleCreateDraft = async () => {
    if (!version) return;
    setActionError(null);
    try {
      const newDraft = await apiClient.post<Version>('/api/versions/draft-from-active', {});
      setVersion(newDraft);
      showToast({ variant: "success", title: "Draft created", description: "A new draft has been created from the active version." });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Create draft failed');
      showToast({ variant: "error", title: "Create draft failed", description: "Could not create a draft. Please try again." });
    }
  };

  const handleArchive = async () => {
    if (!version) return;
    setActionError(null);
    try {
      await apiClient.patch(`/api/versions/${version.id}`, { state: 'Archived' });
      showToast({ variant: "success", title: "Version archived", description: `"${version.version_name}" has been archived.` });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Archive failed');
      showToast({ variant: "error", title: "Archive failed", description: "Could not archive the version. Please try again." });
    }
  };

  const handlePublishClick = () => {
    setPublishOpen(true);
  };

  const handlePublishConfirm = async () => {
    if (!version) return;
    setActionError(null);
    setIsPublishing(true);
    try {
      const published = await apiClient.post<Version & {
        cleanup?: {
          removed_scoring_rule_sets: Array<{ id: string; name: string }>;
          removed_scoring_rule_count: number;
        };
      }>(`/api/versions/${version.id}/publish`, {});
      setVersion(published);
      setPublishOpen(false);
      triggerRefresh();

      // Build a richer success toast when the publish cleanup removed
      // inactive scoring rule sets — the author already saw a warning
      // pre-publish; this is the receipt for what actually happened.
      let description = `"${version.version_name}" is now active.`;
      const cleanup = published.cleanup;
      if (cleanup && cleanup.removed_scoring_rule_sets.length > 0) {
        const n = cleanup.removed_scoring_rule_sets.length;
        const r = cleanup.removed_scoring_rule_count;
        description += ` Removed ${n} inactive scoring rule set${n === 1 ? '' : 's'}` +
          ` (${r} rule${r === 1 ? '' : 's'}) during cleanup.`;
      }
      showToast({ variant: "success", title: "Version published", description });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Publish failed');
      showToast({ variant: "error", title: "Publish failed", description: "Could not publish the version. Please try again." });
    } finally {
      setIsPublishing(false);
    }
  };

  // When the publish dialog opens, fetch the version's scoring rule sets
  // and surface any that will be pruned by the publish-time cleanup.
  // Quietly drop on error (rare; falls back to no warning, which matches
  // the old behaviour).
  useEffect(() => {
    if (!publishOpen || !version) {
      setInactiveSrsToPrune(null);
      return;
    }
    let cancelled = false;
    apiClient
      .get<Array<{ id: string; name?: string; status?: string }>>(
        `/api/scoring-rule-sets?version_id=${version.id}`,
      )
      .then((sets) => {
        if (cancelled) return;
        const inactiveNames = sets
          .filter((s) => (s.status ?? '') !== 'active')
          .map((s) => s.name?.trim() || '(unnamed)');
        setInactiveSrsToPrune(inactiveNames.length > 0 ? inactiveNames : null);
      })
      .catch(() => {
        if (!cancelled) setInactiveSrsToPrune(null);
      });
    return () => {
      cancelled = true;
    };
  }, [publishOpen, version]);

  const isConfigEditable = isDraft && canEdit;

  const handleSaveConfig = async () => {
    if (!versionId) return;
    const errors: typeof configErrors = {};
    if (answerOptionTags.length === 0) errors.answerOptionTags = 'At least one answer option tag is required.';
    if (variantTypes.length === 0) errors.variantTypes = 'At least one question variant type is required.';
    if (thresholds.length === 0) {
      errors.thresholds = 'At least one activation threshold row is required.';
    } else if (thresholds.some(t => !t.points_range.trim() || !t.faculties_activated.trim())) {
      errors.thresholds = 'All threshold rows must have both fields filled in.';
      setShowThresholdRowErrors(true);
    } else if (thresholds.some(t => !isValidPointsRange(t.points_range))) {
      errors.thresholds = 'One or more threshold rows have an invalid points_range format. Expected: 0-5, >20, or >=10.';
      setShowThresholdRowErrors(true);
    }
    if (Object.keys(errors).length > 0) { setConfigErrors(errors); return; }
    setConfigErrors({});
    setShowThresholdRowErrors(false);
    setIsSavingConfig(true);
    try {
      const configPayload = {
        faculty_levels_per_activity: facultyLevels,
        answer_option_tags: answerOptionTags,
        answer_options_per_tag: answerOptionsPerTag,
        variant_types: variantTypes,
        pathways: ['Amplifying', 'Compensating'],
        faculty_activation_thresholds: thresholds,
      };
      await apiClient.post(`/api/versions/${versionId}/config`, configPayload);
      if (storeVersion && storeVersion.id === versionId) {
        setSelectedVersion({ ...storeVersion, ...configPayload });
      }
      showToast({ variant: "success", title: "Configuration saved", description: "Version configuration has been updated." });
    } catch (err) {
      // RI block: shrinking a config array that's still referenced by content.
      if (err instanceof ServiceError && err.errorCode === "ENTITY_HAS_DEPENDENCIES" && err.impacts && err.impacts.length > 0) {
        setRiBlock({
          title: "Cannot save configuration",
          impacts: err.impacts,
        });
      } else {
        setActionError('Failed to save configuration');
        showToast({ variant: "error", title: "Save failed", description: "Could not save configuration. Please try again." });
      }
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleSaveMeta = async () => {
    if (!versionId || !editVersionName.trim()) return;
    setIsSavingMeta(true);
    try {
      const updated = await apiClient.patch<Version>(`/api/versions/${versionId}`, {
        version_name: editVersionName.trim(),
        changelog_notes: editChangelog.trim(),
      });
      setVersion(updated);
      if (storeVersion && storeVersion.id === versionId) {
        setSelectedVersion({ ...storeVersion, version_name: editVersionName.trim(), changelog_notes: editChangelog.trim() });
      }
      triggerRefresh();
      showToast({ variant: "success", title: "Version updated", description: "Version details have been saved." });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to save version details');
      showToast({ variant: "error", title: "Save failed", description: "Could not save version details. Please try again." });
    } finally {
      setIsSavingMeta(false);
    }
  };

  const metaChanged = version && (
    editVersionName.trim() !== (version.version_name ?? '') ||
    editChangelog.trim() !== (version.changelog_notes ?? '')
  );

  const headingDisplay = editVersionName || (version?.version_name ?? 'Version Detail');

  const defaultTab = 'configuration';

  return (
    <main style={{ minHeight: '600px' }} className="p-8 w-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 border border-brand-steel/50 bg-white"
            aria-label="Back to versions"
            onClick={() => navigate('/content-studio/versions')}
          >
            <ArrowLeft size={16} />
          </Button>
          {isDraft && canEdit ? (
            <input
              type="text"
              value={editVersionName}
              onChange={e => setEditVersionName(e.target.value)}
              className="text-lg font-semibold text-gray-900 border border-brand-steel/50 bg-white rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson"
              aria-label="Version name"
            />
          ) : (
            <h1 className="text-lg font-semibold text-gray-900">{headingDisplay}</h1>
          )}
          {version && (
            <StatusBadge
              variant={isDraft ? 'draft' : isActive ? 'active' : 'archived'}
              showDot
            >
              {version.state}
            </StatusBadge>
          )}
        </div>
        {version && (
          <div className="flex gap-2">
            {isDraft && canPublish && (
              <PublishButton
                isPublishing={isPublishing}
                onClick={handlePublishClick}
              />
            )}
            {isActive && canAdmin && (
              <>
                <Button variant="outline" onClick={handleCreateDraft} className="inline-flex items-center gap-2"><Plus size={16} aria-hidden="true" /> Create Draft</Button>
                <Button variant="outline" onClick={handleArchive}>Archive</Button>
              </>

            )}
          </div>
        )}
      </div>

      {/* Publish confirmation dialog */}
      <AlertDialog open={publishOpen} onOpenChange={setPublishOpen}>
        <AlertDialogContent className="border-brand-steel/50">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-semibold text-gray-900">Publish version?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-brand-steel">
              This version will become active. Ensure it is ready for use before publishing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {(validation?.warnings ?? []).length > 0 && (
            <div className="rounded-lg border border-brand-warning/50 bg-brand-highlight/40 p-3 mt-2">
              <p className="text-sm font-medium text-amber-800 mb-1.5">
                {(validation?.warnings ?? []).length} non-blocking {((validation?.warnings ?? []).length === 1) ? 'warning' : 'warnings'}
              </p>
              <ul className="list-disc pl-5 space-y-1">
                {(validation?.warnings ?? []).slice(0, 8).map((w, i) => (
                  <li key={i} className="text-xs text-amber-800">{w.condition}</li>
                ))}
                {(validation?.warnings ?? []).length > 8 && (
                  <li className="text-xs text-amber-800/70 list-none italic">
                    + {(validation?.warnings ?? []).length - 8} more
                  </li>
                )}
              </ul>
            </div>
          )}
          {inactiveSrsToPrune && inactiveSrsToPrune.length > 0 && (
            <div className="rounded-lg border border-brand-error/40 bg-brand-error/10 p-3 mt-2">
              <p className="text-sm font-medium text-brand-error mb-1.5">
                Publishing will permanently remove {inactiveSrsToPrune.length} inactive scoring rule
                set{inactiveSrsToPrune.length === 1 ? '' : 's'} and any rules within {inactiveSrsToPrune.length === 1 ? 'it' : 'them'}:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                {inactiveSrsToPrune.slice(0, 5).map((name, i) => (
                  <li key={i} className="text-xs text-brand-error">{name}</li>
                ))}
                {inactiveSrsToPrune.length > 5 && (
                  <li className="text-xs text-brand-error/70 list-none italic">
                    + {inactiveSrsToPrune.length - 5} more
                  </li>
                )}
              </ul>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="border-brand-steel/50 text-brand-steel hover:bg-brand-panel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-brand-crimson hover:bg-brand-crimson/90 font-medium text-sm text-white"
              onClick={handlePublishConfirm}
            >
              Publish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {isLoading && (
        <PageSpinner label="Loading version" />
      )}

      {loadError && (
        <div role="alert" className="mb-4 p-3 bg-red-50 border border-brand-error/40 rounded text-sm text-brand-error">
          {loadError}
        </div>
      )}

      {!version && !isLoading && !loadError && (
        <div role="alert" className="p-4 bg-red-50 border border-brand-error/40 rounded text-sm text-brand-error">
          Version not found.
        </div>
      )}

      {actionError && (
        <div role="alert" className="mb-4 p-3 bg-red-50 border border-brand-error/40 rounded text-sm text-brand-error">
          {actionError}
        </div>
      )}

      {/* Changelog — editable for Draft, read-only otherwise */}
      {version && (isDraft && canEdit ? (
        <div className="mb-6 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Changelog</label>
            <textarea
              value={editChangelog}
              onChange={e => setEditChangelog(e.target.value)}
              rows={3}
              placeholder="Describe what changed in this version..."
              className="w-full max-w-xl border border-brand-steel/50 bg-white rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-brand-steel/50 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson"
              aria-label="Changelog notes"
            />
          </div>
          {metaChanged && (
            <Button onClick={handleSaveMeta} disabled={isSavingMeta || !editVersionName.trim()}>
              {isSavingMeta && <Loader2 size={16} className="animate-spin mr-2" aria-hidden="true" />}
              {isSavingMeta ? 'Saving...' : 'Save Details'}
            </Button>
          )}
        </div>
      ) : version.changelog_notes ? (
        <div className="mb-6">
          <h2 className="text-sm font-medium text-gray-700 mb-1">Changelog</h2>
          <p className="text-sm text-brand-steel">{version.changelog_notes}</p>
        </div>
      ) : null)}

      {/* Tabs wrapped in Card */}
      {version && (
        <Card className="border-brand-steel/50 bg-white shadow-sm">
          <CardContent className="px-5 py-1">
            <Tabs defaultValue={defaultTab}>
              <TabsList>
                <TabsTrigger value="configuration">Configuration</TabsTrigger>
                <TabsTrigger value="validation">Validation Results</TabsTrigger>
                <TabsTrigger value="audit">Audit History</TabsTrigger>
              </TabsList>

              {/* Configuration tab */}
              <TabsContent value="configuration">
                <div className="py-4 max-w-3xl space-y-6">
                  {!isConfigEditable && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                      Configuration is read-only for {version.state} versions.
                    </div>
                  )}

                  {/* Number fields */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Disposition Levels per Activity</label>
                      <input
                        type="number"
                        min={1}
                        max={10}
                        value={facultyLevels}
                        onChange={e => setFacultyLevels(Math.max(1, Number(e.target.value)))}
                        disabled={!isConfigEditable}
                        className="w-full h-9 border border-brand-steel/50 bg-white rounded-lg px-3 text-sm text-gray-900 disabled:opacity-50 disabled:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson"
                        aria-label="Disposition levels per activity"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Answer Options per Tag</label>
                      <input
                        type="number"
                        min={1}
                        max={10}
                        value={answerOptionsPerTag}
                        onChange={e => setAnswerOptionsPerTag(Math.max(1, Number(e.target.value)))}
                        disabled={!isConfigEditable}
                        className="w-full h-9 border border-brand-steel/50 bg-white rounded-lg px-3 text-sm text-gray-900 disabled:opacity-50 disabled:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson"
                        aria-label="Answer options per tag"
                      />
                    </div>
                  </div>

                  <div>
                    <TagChips
                      label="Answer Option Tags"
                      tags={answerOptionTags}
                      onAdd={tag => { setAnswerOptionTags(prev => [...prev, tag]); setConfigErrors(e => ({ ...e, answerOptionTags: undefined })); }}
                      onRemove={idx => setAnswerOptionTags(prev => prev.filter((_, i) => i !== idx))}
                      readOnly={!isConfigEditable}
                      placeholder="Add tag (e.g. Crux)..."
                    />
                    {configErrors.answerOptionTags && (
                      <p className="text-xs text-brand-error mt-1">{configErrors.answerOptionTags}</p>
                    )}
                  </div>

                  <div>
                    <TagChips
                      label="Question Variant Types"
                      tags={variantTypes}
                      onAdd={tag => { setVariantTypes(prev => [...prev, tag]); setConfigErrors(e => ({ ...e, variantTypes: undefined })); }}
                      onRemove={idx => setVariantTypes(prev => prev.filter((_, i) => i !== idx))}
                      readOnly={!isConfigEditable}
                      placeholder="Add variant type..."
                    />
                    {configErrors.variantTypes && (
                      <p className="text-xs text-brand-error mt-1">{configErrors.variantTypes}</p>
                    )}
                  </div>

                  {/* Pathways — system-defined, always read-only */}
                  <TagChips
                    label="Pathways"
                    tags={['Amplifying', 'Compensating']}
                    onAdd={() => {}}
                    onRemove={() => {}}
                    readOnly
                  />

                  <div>
                    <ThresholdTable
                      thresholds={thresholds}
                      onChange={next => {
                        setThresholds(next);
                        if (next.length > 0) setConfigErrors(e => ({ ...e, thresholds: undefined }));
                        if (next.every(t => t.points_range.trim() && t.faculties_activated.trim())) setShowThresholdRowErrors(false);
                      }}
                      readOnly={!isConfigEditable}
                      showRowErrors={showThresholdRowErrors}
                    />
                    {configErrors.thresholds && (
                      <p className="text-xs text-brand-error mt-1">{configErrors.thresholds}</p>
                    )}
                  </div>

                  {isConfigEditable && (
                    <div className="flex justify-end pt-2">
                      <Button onClick={handleSaveConfig} disabled={isSavingConfig}>
                        {isSavingConfig && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
                        {isSavingConfig ? 'Saving...' : 'Save Configuration'}
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>

          {/* Validation Results tab */}
          <TabsContent value="validation">
            {validation ? (
              <div className="py-4">
                {/* Status badge */}
                <div className="flex items-center gap-2.5 mb-4">
                  <div className={`flex items-center justify-center w-9 h-9 rounded-lg ${
                    validation.status === 'all_passed' ? 'bg-brand-success' : 'bg-brand-error'
                  }`}>
                    <ShieldCheck size={18} className="text-white" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {validation.status === 'all_passed' ? 'All checks passed' : 'Blocking issues found'}
                    </p>
                    <p className="text-xs text-brand-steel">
                      {(validation.checks ?? []).filter(c => c.passed).length} of {(validation.checks ?? []).length} checks passed
                    </p>
                  </div>
                </div>

                {/* Blocking errors */}
                {(validation.blocking_errors ?? validation.blocking_conditions ?? []).length > 0 && (
                  <div role="alert" className="mb-4 p-3 bg-red-50 border border-brand-error/40 rounded-lg">
                    <p className="text-xs font-semibold text-brand-error mb-1">Blocking errors:</p>
                    <ul className="list-disc pl-4 space-y-0.5">
                      {(validation.blocking_errors ?? validation.blocking_conditions ?? []).map((err, i) => (
                        <li key={i} className="text-xs text-brand-error">{err.condition}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Checks list */}
                {(validation.checks ?? []).length > 0 && (
                  <ul className="space-y-2">
                    {(validation.checks ?? []).map(check => (
                      <li
                        key={check.name}
                        className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${
                          check.passed ? 'bg-green-50 border-brand-success/30' : 'bg-red-50 border-brand-error/30'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          {check.passed ? (
                            <Check size={16} className="text-brand-success shrink-0" aria-hidden="true" />
                          ) : (
                            <X size={16} className="text-brand-error shrink-0" aria-hidden="true" />
                          )}
                          <span className={`text-sm font-medium ${check.passed ? 'text-green-800' : 'text-red-800'}`}>
                            {check.name.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold ${
                          check.passed ? 'bg-brand-success/10 text-brand-success' : 'bg-brand-error/10 text-brand-error'
                        }`}>
                          {check.passed ? 'Pass' : 'Fail'}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <p className="text-sm text-brand-steel py-4">Loading validation results...</p>
            )}
          </TabsContent>

          {/* Audit History tab */}
          <TabsContent value="audit">
            {auditEntries.length > 0 ? (
              <div className="py-4">
                <div className="border border-brand-steel/20 rounded-lg divide-y divide-brand-steel/20">
                  {auditEntries.map(entry => {
                    const badge = getActionPresentation(entry);
                    // The Version Detail page already has the version
                    // loaded, so we can render "Published version: <name>"
                    // instead of "<version_id>" for any Version-scoped
                    // event without needing entity_name embedded in the row.
                    const versionName =
                      entry.entity_type === "Version" ? version?.version_name : undefined;
                    const fieldDiff =
                      entry.fields && entry.fields.length > 0
                        ? formatFieldSummary(entry.fields)
                        : formatChangeSummary(entry);
                    return (
                      <div key={entry.id} className="flex items-start gap-4 px-4 py-3.5">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-semibold border mt-0.5 shrink-0 w-18 justify-center ${badge.classes}`}>
                          {badge.label}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {formatAuditTitle(entry, versionName)}
                          </p>
                          <p className="text-xs text-brand-steel mt-0.5">
                            {getCategoryLabel(entry.entity_type)}
                            {fieldDiff && (
                              <> &middot; {fieldDiff}</>
                            )}
                            {" "}&middot; {entry.changed_by} &middot; {formatRelativeTime(entry.changed_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-sm text-brand-steel py-4">No audit entries yet. Make changes to see activity here.</p>
            )}
          </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      <RIBlockedDialog
        open={!!riBlock}
        onOpenChange={(o) => { if (!o) setRiBlock(null); }}
        title={riBlock?.title ?? "Cannot save configuration"}
        description="Existing content still references values that the new configuration would disallow. Resolve these before retrying."
        impacts={riBlock?.impacts ?? []}
      />
    </main>
  );
}

export default VersionDetailPage;
